import type { FeatureFlagKey } from '@vizit/constants';
import { useFlags, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import type { LDClient, LDContext, LDFlagSet } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { DEFAULT_FLAGS } from '../default-flags';
import { flagOverrides, initializeOverrideInterface } from '../overrides/overrides';
import { setFeatureFlagClient } from '../utils/require-feature-flags';

interface FeatureFlagContextValue {
  getFlag: <T = boolean>(flagKey: FeatureFlagKey, defaultValue: T) => T;
  ldClient?: LDClient;
  allFlags: LDFlagSet;
}

export const FeatureFlagContext = React.createContext<FeatureFlagContextValue | null>(null);

interface FeatureFlagProviderProps {
  children: React.ReactNode;
  defaultFlags?: Partial<Record<FeatureFlagKey, boolean | string | number>>;
}

/**
 * Simple feature flag provider that wraps LaunchDarkly
 */
function FeatureFlagProviderInner({ children }: { children?: React.ReactNode }) {
  const ldClient = useLDClient();
  // The SDK's own flag state changes on `change` and also when a load that outran the provider's timeout
  // finishes, which fires `ready` but no `change`. It only marks when to read the client again.
  const ldFlags = useFlags();

  // biome-ignore lint/correctness/useExhaustiveDependencies: ldFlags invalidates the read of the client's flags
  const allFlags = React.useMemo<LDFlagSet>(() => ldClient?.allFlags() ?? {}, [ldClient, ldFlags]);

  React.useEffect(() => {
    initializeOverrideInterface(ldClient);
  }, [ldClient]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: allFlags invalidates the closure over the client's flags
  const getFlag = React.useCallback(
    <T,>(flagKey: FeatureFlagKey, defaultValue: T): T => {
      // Check overrides first
      if (flagKey in flagOverrides) {
        return flagOverrides[flagKey] as T;
      }

      // Fall back to LaunchDarkly or default
      if (ldClient) {
        return ldClient.variation(flagKey, DEFAULT_FLAGS[flagKey] ?? defaultValue) as T;
      }

      return defaultValue;
    },
    [ldClient, allFlags],
  );

  const contextValue = React.useMemo(
    () => ({
      getFlag,
      ldClient,
      allFlags,
    }),
    [getFlag, ldClient, allFlags],
  );

  React.useEffect(() => {
    if (ldClient) {
      setFeatureFlagClient(ldClient, contextValue);
    }
  }, [ldClient, contextValue]);

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
}

/** The user LaunchDarkly evaluates flags for. */
export interface FeatureFlagUser {
  key: string;
  email?: string;
  organizationId?: string;
  workspaceId?: string;
}

export function toFeatureFlagContext(user: FeatureFlagUser): LDContext {
  return {
    kind: 'user',
    key: user.key,
    email: user.email,
    organizationId: user.organizationId,
    workspaceId: user.workspaceId,
    _meta: {
      privateAttributes: ['/email'],
    },
  };
}

/**
 * Create the LaunchDarkly provider with config
 */
export function createFeatureFlagProvider(config: {
  clientSideId: string;
  user?: FeatureFlagUser;
}): React.ComponentType<{ children: React.ReactNode }> {
  const { clientSideId, user = { key: 'anonymous' } } = config;

  const LDProvider = withLDProvider({
    clientSideID: clientSideId,
    context: toFeatureFlagContext(user),
    options: {
      streaming: true,
    },
    timeout: 2,
  })(FeatureFlagProviderInner);

  // Type assertion to fix LaunchDarkly's type inference
  return LDProvider as unknown as React.ComponentType<{ children: React.ReactNode }>;
}

/** A flag value the override store accepts: boolean, string, or number. */
type FlagValue = boolean | string | number;

function isFlagValue(value: unknown): value is FlagValue {
  return typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number';
}

/** Merge the devlogin-seeded local snapshot. Any invalid entry rejects the whole snapshot. */
function loadLocalFlagSnapshot(): boolean {
  // Dev builds only, so a deployed build that lands here cannot read local overrides.
  if (!import.meta.env.DEV) return false;
  try {
    const stored = window.localStorage.getItem('local_feature_flags');
    if (!stored) return false;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
    const entries = Object.entries(parsed);
    if (entries.length === 0 || !entries.every(([, value]) => isFlagValue(value))) return false;
    for (const [key, value] of entries) {
      flagOverrides[key] = value as FlagValue;
    }
    return true;
  } catch {
    // A bad snapshot falls back to the built-in defaults.
    return false;
  }
}

// Fallback provider when LaunchDarkly is not configured
export function FeatureFlagFallbackProvider({ children, defaultFlags = DEFAULT_FLAGS }: FeatureFlagProviderProps) {
  // Bumped whenever the override store mutates, so getFlag and the memoized
  // context value change identity and consumers re-render.
  const [overrideRevision, bumpOverrideRevision] = React.useReducer((x) => x + 1, 0);

  React.useEffect(() => {
    // Local dev: the vizit-api devlogin sidecar seeds a flag snapshot synced
    // from a real org (make sync-ff); it beats the built-in defaults.
    if (loadLocalFlagSnapshot()) {
      bumpOverrideRevision();
    }

    // Initialize override interface on mount
    initializeOverrideInterface();

    // Listen for override changes
    const handleOverrideChange = () => {
      bumpOverrideRevision();
    };

    window.addEventListener('flag-override-changed', handleOverrideChange);
    return () => window.removeEventListener('flag-override-changed', handleOverrideChange);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: overrideRevision invalidates the closure over the mutable flagOverrides store
  const getFlag = React.useCallback(
    <T,>(flagKey: string, defaultValue: T): T => {
      // Check overrides first
      if (flagKey in flagOverrides) {
        return flagOverrides[flagKey] as T;
      }

      // Use default flags if available
      if (flagKey in defaultFlags) {
        return defaultFlags[flagKey as keyof typeof DEFAULT_FLAGS] as T;
      }

      // Fall back to provided default
      return defaultValue;
    },
    [defaultFlags, overrideRevision],
  );

  const contextValue = React.useMemo(
    () => ({
      getFlag,
      ldClient: undefined,
      allFlags: DEFAULT_FLAGS as LDFlagSet,
    }),
    [getFlag],
  );

  // Route guards await this; without it the fallback path stalls to the timeout.
  React.useEffect(() => {
    setFeatureFlagClient(null, contextValue);
  }, [contextValue]);

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
}

export type { FeatureFlagContextValue };
